import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { FacebookIcon, AppleIcon } from '../components/OAuthIcons';
import { useAuthStore } from '../stores/authStore';
import { useRedirectIfAuthenticated } from '../hooks/useRedirectIfAuthenticated';

const emailPasswordEnabled = import.meta.env.VITE_EMAIL_PASSWORD_ENABLED === 'true';

const schema = z.object({ email: z.string().email('Enter a valid email') });
type FormData = z.infer<typeof schema>;
// @ts-expect-error — zodResolver type instantiation is too deep with strict settings
const resolver = zodResolver(schema);

const passwordSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});
type PasswordFormData = z.infer<typeof passwordSchema>;
// @ts-expect-error — zodResolver type instantiation is too deep with strict settings
const passwordResolver = zodResolver(passwordSchema);

export function LoginPage() {
  const { signIn, signInWithOAuth, signInWithPassword } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [sent, setSent] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);
  const [pwError, setPwError] = useState<string | null>(null);

  const redirectTo = searchParams.get('redirect') ?? '/dashboard';
  useRedirectIfAuthenticated(redirectTo);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver,
  });

  const {
    register: registerPw,
    handleSubmit: handleSubmitPw,
    formState: { errors: pwErrors, isSubmitting: pwSubmitting },
  } = useForm<PasswordFormData>({ resolver: passwordResolver });

  const onSubmit = async (data: FormData) => {
    const result = await signIn(data.email);
    if (!result.error) setSent(true);
  };

  const onPasswordSubmit = async (data: PasswordFormData) => {
    setPwError(null);
    const result = await signInWithPassword(data.email, data.password);
    if (result.error) setPwError(result.error);
  };

  const handleOAuth = async (provider: 'google' | 'facebook' | 'apple') => {
    setOauthLoading(provider);
    await signInWithOAuth(provider);
    setOauthLoading(null);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" fontWeight={700} mb={3} textAlign="center">
            Sign in
          </Typography>

          {sent ? (
            <Alert severity="success">
              Magic link sent! Check your email to sign in.
            </Alert>
          ) : (
            <>
              <Stack spacing={2} mb={3}>
                <Button
                  variant="outlined"
                  startIcon={
                    oauthLoading === 'google' ? (
                      <CircularProgress size={16} />
                    ) : (
                      <GoogleIcon />
                    )
                  }
                  onClick={() => handleOAuth('google')}
                  disabled={!!oauthLoading}
                  fullWidth
                >
                  Continue with Google
                </Button>
                <Button
                  variant="outlined"
                  startIcon={
                    oauthLoading === 'facebook' ? (
                      <CircularProgress size={16} />
                    ) : (
                      <FacebookIcon />
                    )
                  }
                  onClick={() => handleOAuth('facebook')}
                  disabled={!!oauthLoading}
                  fullWidth
                >
                  Continue with Facebook
                </Button>
                <Button
                  variant="outlined"
                  startIcon={
                    oauthLoading === 'apple' ? (
                      <CircularProgress size={16} />
                    ) : (
                      <AppleIcon />
                    )
                  }
                  onClick={() => handleOAuth('apple')}
                  disabled={!!oauthLoading}
                  fullWidth
                >
                  Continue with Apple
                </Button>
              </Stack>

              <Divider sx={{ mb: 3 }}>or use email</Divider>

              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <TextField
                  {...register('email')}
                  label="Email address"
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={{ mb: 2 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isSubmitting}
                  startIcon={
                    isSubmitting ? <CircularProgress size={16} /> : undefined
                  }
                >
                  Send magic link
                </Button>
              </Box>
            </>
          )}
          {emailPasswordEnabled && !sent && (
            <>
              <Divider sx={{ my: 3 }}>or sign in with password</Divider>
              <Box component="form" onSubmit={handleSubmitPw(onPasswordSubmit)}>
                <TextField
                  {...registerPw('email')}
                  label="Email address"
                  type="email"
                  fullWidth
                  error={!!pwErrors.email}
                  helperText={pwErrors.email?.message}
                  sx={{ mb: 2 }}
                />
                <TextField
                  {...registerPw('password')}
                  label="Password"
                  type="password"
                  fullWidth
                  error={!!pwErrors.password}
                  helperText={pwErrors.password?.message}
                  sx={{ mb: 2 }}
                />
                {pwError && <Alert severity="error" sx={{ mb: 2 }}>{pwError}</Alert>}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={pwSubmitting}
                  startIcon={pwSubmitting ? <CircularProgress size={16} /> : undefined}
                >
                  Sign in
                </Button>
                <Box textAlign="right" mt={1}>
                  <Button component={RouterLink} to="/forgot-password" size="small" variant="text">
                    Forgot password?
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
